import React, { useCallback, useSyncExternalStore, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { getPlatform } from '../platform';
import {
  getPayzheBtpProviderSnapshot,
  payzheBtpProvider,
  subscribePayzheBtpProvider,
} from '../native-btp/PayzheBtpProvider';
import { bootstrapPayzheBtpBridge } from '../native-btp/payzheBtpMobileBootstrap';
import { fetchPayzheBtpPolicy } from '../native-btp/payzheBtpSettings';
import {
  fetchApkDownloadInfo,
  formatApkSize,
  openPayzheBtpApkDownload,
  resolveApkDownloadUrl,
} from '../native-btp/downloadApk';
import { wakePayzheBtpApp } from '../native-btp/mobilePlatform';
import { postPayzheBtpServiceRestart, getStoredPayzheBtpToken } from '../native-btp/nativeBtpClient';

export const PayzheBtpStatusBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [installError, setInstallError] = useState<string | null>(null);
  const [apkSizeHint, setApkSizeHint] = useState<string | undefined>(undefined);
  const [retryBusy, setRetryBusy] = useState(false);

  const subscribe = useCallback((onChange: () => void) => subscribePayzheBtpProvider(onChange), []);
  const getSnapshot = useCallback(() => getPayzheBtpProviderSnapshot(), []);
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (!isAuthenticated || getPlatform() !== 'android') {
    return null;
  }

  if (snap.state === 'ready' && snap.status?.connected) {
    return null;
  }

  if (snap.state === 'idle' || snap.state === 'checking') {
    return null;
  }

  const handleInstall = async () => {
    setInstallError(null);
    try {
      const policy = snap.policy ?? (await fetchPayzheBtpPolicy());
      const url = resolveApkDownloadUrl(policy);
      const preflight = await fetchApkDownloadInfo(url);
      const sizeLabel = formatApkSize(preflight.sizeBytes);
      if (sizeLabel) setApkSizeHint(sizeLabel);
      await openPayzheBtpApkDownload(url);
    } catch (err) {
      setInstallError(err instanceof Error ? err.message : t('bluetoothPrinter.payzheBtp.banner.installFailed'));
    }
  };

  const handleWake = async () => {
    wakePayzheBtpApp();
    const token = getStoredPayzheBtpToken();
    if (token) {
      await postPayzheBtpServiceRestart(token).catch(() => undefined);
    }
    await payzheBtpProvider.ensureReady({ wakeApp: true }).catch(() => undefined);
  };

  const handleRetryBridge = async () => {
    setRetryBusy(true);
    try {
      await bootstrapPayzheBtpBridge({ wakeApp: true, connectPrinter: true });
    } finally {
      setRetryBusy(false);
    }
  };

  let message = '';
  let action: React.ReactNode = null;
  let showInstallHints = false;

  if (snap.state === 'unreachable') {
    message = t('bluetoothPrinter.payzheBtp.banner.notInstalled');
    showInstallHints = true;
    action = (
      <>
        <button
          type="button"
          className="underline font-medium disabled:opacity-60"
          disabled={retryBusy}
          onClick={() => void handleRetryBridge()}
        >
          {retryBusy
            ? t('bluetoothPrinter.payzheBtp.refreshBusy')
            : t('bluetoothPrinter.payzheBtp.banner.retryConnection')}
        </button>
        <span className="mx-1">·</span>
        <button type="button" className="underline font-medium" onClick={() => void handleInstall()}>
          {t('bluetoothPrinter.payzheBtp.downloadApk')}
        </button>
      </>
    );
  } else if (snap.state === 'not_running') {
    message = t('bluetoothPrinter.payzheBtp.banner.notRunning');
    action = (
      <>
        <button type="button" className="underline font-medium" onClick={() => void handleWake()}>
          {t('bluetoothPrinter.payzheBtp.openApp')}
        </button>
        <span className="mx-1">·</span>
        <button
          type="button"
          className="underline font-medium disabled:opacity-60"
          disabled={retryBusy}
          onClick={() => void handleRetryBridge()}
        >
          {retryBusy
            ? t('bluetoothPrinter.payzheBtp.refreshBusy')
            : t('bluetoothPrinter.payzheBtp.banner.retryConnection')}
        </button>
      </>
    );
  } else if (snap.state === 'outdated') {
    const incompatibleWeb =
      snap.lastErrorKey === 'bluetoothPrinter.payzheBtp.errors.incompatibleWeb';
    message = incompatibleWeb
      ? t('bluetoothPrinter.payzheBtp.banner.incompatibleWeb')
      : t('bluetoothPrinter.payzheBtp.banner.outdated', {
          version: snap.version?.version ?? '?',
        });
    if (incompatibleWeb) {
      action = (
        <button type="button" className="underline font-medium" onClick={() => globalThis.location.reload()}>
          {t('bluetoothPrinter.payzheBtp.banner.reloadWeb')}
        </button>
      );
    } else {
      showInstallHints = true;
      action = (
        <button type="button" className="underline font-medium" onClick={() => void handleInstall()}>
          {t('bluetoothPrinter.payzheBtp.downloadApk')}
        </button>
      );
    }
  } else if (snap.state === 'service_stopped') {
    message = t('bluetoothPrinter.payzheBtp.banner.serviceStopped');
    action = (
      <button type="button" className="underline font-medium" onClick={() => void handleWake()}>
        {t('bluetoothPrinter.payzheBtp.banner.startService')}
      </button>
    );
  } else if (snap.state === 'ready' && !snap.status?.connected) {
    message = t('bluetoothPrinter.payzheBtp.banner.printerDisconnected');
    action = (
      <>
        <button
          type="button"
          className="underline font-medium disabled:opacity-60"
          disabled={retryBusy}
          onClick={() => void handleRetryBridge()}
        >
          {retryBusy
            ? t('bluetoothPrinter.payzheBtp.refreshBusy')
            : t('bluetoothPrinter.payzheBtp.banner.retryConnection')}
        </button>
        <span className="mx-1">·</span>
        <Link to="/thermal-printer" className="underline font-medium">
          {t('bluetoothPrinter.payzheBtp.banner.connectPrinter')}
        </Link>
      </>
    );
  } else {
    return null;
  }

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950"
      role="status"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>{message}</span>
        {action}
      </div>
      {showInstallHints ? (
        <p className="mt-1 text-xs text-amber-900/90">
          {t('bluetoothPrinter.payzheBtp.downloadApkHint')}
          {apkSizeHint ? ` · ${t('bluetoothPrinter.payzheBtp.banner.apkSizeHint', { size: apkSizeHint })}` : ''}
        </p>
      ) : null}
      {showInstallHints ? (
        <p className="mt-0.5 text-xs text-amber-900/80">
          {t('bluetoothPrinter.payzheBtp.banner.parseErrorHint')}
        </p>
      ) : null}
      {installError ? (
        <p className="mt-1 text-xs text-red-700">{installError}</p>
      ) : null}
    </div>
  );
};

export default PayzheBtpStatusBanner;
